import { Fragment, memo, useCallback, useMemo } from 'react';
import { goToModelsYouMayLike } from '../../../../actions/nav';
import { MYML_LOCATIONS, ViewMode } from '../../../../constants/enums';
import { locales } from '../../../../locales/en';
import { isLounge } from '../../../../mfcLogic/userProperties/user';
import Button from '../../../Buttons/Button';
import { AppStateType, useAppStateSelector } from '../../../Hooks/useAppState';
import { useFloatingRoomStateSelector } from '../../../Hooks/useModal';
import { useModelColor } from '../../../Hooks/useModelColor';
import useModelsYouMayLike from '../../../Hooks/useModelsYouMayLike';
import { useUserSelector } from '../../../Hooks/useUser';
import { noResultStyles } from '../shared';
import Grid from './Grid';
import List from './List';
import "./Recommendation.linaria.module.css!=!./Recommendation?./Recommendation.linaria.module.css?css=H4sIAAAAAAAAA41Vy66bMBDd9yuyqXSvKiIbzCN00z+xABtjYmweDoaL+u9VQsKraWHB4px5HDwzHp8jXeBLg6HuDG/FQHhTiqgPU0G7n7+/ne/mqMYwLpG6Jhs43L0swmuaaK5kmChxK+QrrO0xVE2l7HYwnOgshAB8fxmpPRkzylmmV9bawTDzVH77GjTttBUJzmSYUKlp/fJJGhynkql0jYZUSW2ZMacLwMudVTjVjAt7jYYiqhmXVqy0VkUYlPdT/yoo4dHpo4g6a/z1E/JB2X0Oj2DJUOx4mIhr0LVHmPGfGv5FQ4juCu8lwCzhuygLUgxT08mWHqOWKvao8vASCsMeISXSDRzKiBAumaVVGT7+awy4j8NN0C6Np3GIhUqui8ayjPmgfPbVRmAdDEvXpGAKZjUnL7N0MbeNRmiNhloZi0Vl6M2ZArR0ndAwTtnWm3xhaLwLYGIDh7u8pWlRikhTa4xuQsf5fnp+00wCDM2NNdLdwE2KWpkmjG5avR8V3/OnPgqvdPorZqI3afE3cyK8DaXOrCTjgnzI048T+pzqJpWkq1nhctYIJg0IS/NFCgwJCBIjjnL/OtRpPNm+bJVXgecub9UutexdsLkI71XynrXIXmbcYaZJOpYfQtRdRIphzHgWV++4/zcpUVJTqZvpumXO6rrN8LVqNrctabDkvgBsjVZ7zJv3WB5Pi3M+0nN3vhlGb7FUlGO8vFxWbod5txx3VWDV9jDvMdR1XunLUW61vc7uISlKHEAVdnMZPJ+BHWYQXFLr9eCcL97UNZtgJrvkelmjIY6SK6vVTZL75lB12Eb1h2WROjK0tmbr5/vyQ2CjuTKG262p11XY545WZqXVXIuLzpaF2GHelmb3RN7NZ1RiTsqUoWPU6tUBR1T6KFO6WmbcYSaJ5+AeUim072XpMucOM6kImupjGlUFSg2XGXeYSaN+9OUh8gft6tLkswkAAA=="
const rootStyles = "atm_9s_1txwivl atm_ar_1bp4okc atm_vy_1osqo2v atm_e2_1osqo2v";
const titleStyles = "atm_r3_1h6ojuz atm_cs_bfngof atm_gq_ftgil2 atm_ng4b36_dlk8xv atm_754h8f_1fwxnve";
const gridButtonStyles = "atm_lo_1y44olf atm_9s_1ulexfb atm_vy_ghg70p";

/** Hide elements depending on the screen size */
const gridStyles = "atm_9s_11p5wf0 atm_n5_i2wt44 atm_84_i2wt44 atm_dz_1w690gl atm_e0_1wugsn5 atm_l6p3yk_glywfm atm_11pwzdm_1d08cwl atm_1qjq865_ftgil2 atm_jygv42_ftgil2 atm_114x9lf_1bgihbq";
const moreButtonStyles = "atm_h3_1y44olf atm_cs_ni7l0g atm_jb_1osqo2v atm_o3w6jp_ftgil2 atm_1qvy1jy_1trjqt9 atm_ed30eo_5jn8of";
const morePosterButtonStyles = "atm_2d_gnxck9 atm_cs_ni7l0g atm_jb_1osqo2v atm_1wi2vwr_1trjqt9 atm_skm9th_5jn8of atm_16u7gen_idpfg4 atm_yahotq_idpfg4 atm_mt76hf_idpfg4 atm_qq0pt1_idpfg4";
interface RecommendationProps {
  hideNoResultNotice?: boolean;
  id: number;
  maxResultCount?: number;
  inRoomOrProfile?: boolean;
  showAsGrid?: boolean;
  location: MYML_LOCATIONS;
}
const Title = ({
  color,
  name
}: {
  color: string;
  name: string;
}) => {
  return <div className={titleStyles}>
            {locales.modelsYouMayLike.ifYouLike}{' '}
            <span style={{
      color
    }}>
                {name}
            </span>
            {locales.modelsYouMayLike.resultMessageEnd}
        </div>;
};
const Content = ({
  id,
  inRoomOrProfile = false,
  hideNoResultNotice = false,
  maxResultCount = 6,
  showAsGrid = false,
  location
}: RecommendationProps) => {
  const {
    modelResults
  } = useModelsYouMayLike(id, location);
  const RecommendationComponent = showAsGrid ? Grid : List;
  const modelColor = useUserSelector(id, 'color');
  const name = useUserSelector(id, 'name');
  const color = useModelColor(modelColor);
  const inLounge = isLounge(name);
  const topModels = useMemo(() => modelResults.slice(0, maxResultCount), [modelResults, maxResultCount]);
  const onOpenMoreModelsYouMayLike = useCallback(() => {
    goToModelsYouMayLike(name);
  }, [name]);
  if (topModels.length === 0) {
    if (hideNoResultNotice) return <Fragment />;
    return <article data-testid="ModelsYouMayLike__NoResults" className={noResultStyles}>
                <p>
                    {locales.modelsYouMayLike.noRecommendations}{' '}
                    <span style={{
          color,
          fontWeight: 'bold'
        }}>{name}</span>.
                </p>
            </article>;
  }
  return <>
            <Title name={name} color={color} />
            <article key={id} data-testid="ModelsYouMayLike__ResultRows" className={showAsGrid ? gridStyles : null}>
                {topModels.map(model => <div key={model.user_id}>
                        <RecommendationComponent id={model.user_id} recommendation={model.similarity} inRoomOrProfile={inRoomOrProfile} />
                    </div>)}
            </article>
            {!inLounge && <article className={showAsGrid ? gridButtonStyles : null}>
                    <Button data-testid="ModelsYouMayLike__Button" onClick={onOpenMoreModelsYouMayLike} size="medium" border clean className={showAsGrid ? morePosterButtonStyles : moreButtonStyles}>
                        {locales.modelsYouMayLike.moreModels}
                    </Button>
                </article>}
        </>;
};
const Recommendation = (props: RecommendationProps) => {
  const {
    id,
    showAsGrid
  } = props;
  const viewMode = useFloatingRoomStateSelector('viewMode');
  const fullWindow = viewMode === ViewMode.FullWindow;
  const isMobile = useAppStateSelector(AppStateType.APP, 'isMobile');
  // hide when in full window mode on mobile due to chat overlap
  const hideDueToSizeConstraints = fullWindow && isMobile && showAsGrid;
  if (hideDueToSizeConstraints) return null;
  return <div style={showAsGrid ? {
    alignItems: 'center'
  } : {}} key={id} data-testid="ModelsYouMayLike__Container" className={rootStyles}>
            <Content {...props} />
        </div>;
};
export default memo(Recommendation);